import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { getAssignee, type Assignee } from '../lib/assignees';
import { AssigneeForm } from '../components/assignees/AssigneeForm';
import { Alert, BackLink, EmptyState, LinkButton, PageHeader, SkeletonRows } from '../components/ui';

/**
 * Serves both `/assignees/new` and `/assignees/:id`. There is no detail view to
 * hang an Edit tab off, so one page does both — the form itself already handles
 * create and edit from whether it was given a record.
 */
export function AssigneeEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assignee, setAssignee] = useState<Assignee | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);

  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    getAssignee(id, controller.signal)
      .then((data) => {
        setAssignee(data.assignee);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(requestError);
      });

    return () => controller.abort();
  }, [id]);

  const notFound = loadError instanceof ApiError && loadError.status === 404;

  return (
    <div>
      {/*
        The title used to be `assignee?.name ?? 'Assignee'`, so opening somebody's
        page showed the literal word "Assignee" for a beat and then flashed to
        their name. A bar of the right size says the same thing -- "this is who,
        we do not know yet" -- without printing a word that is not their name.
      */}
      <PageHeader
        eyebrow={<BackLink to="/assignees">Assignees</BackLink>}
        title={
          id ? (
            (assignee?.name ?? (
              <span
                aria-hidden="true"
                className="inline-block h-6 w-48 max-w-full animate-pulse rounded bg-stone-200 align-middle"
              />
            ))
          ) : (
            'New assignee'
          )
        }
      />

      <div className="mt-6 max-w-2xl">
        {notFound ? (
          <EmptyState
            title="Assignee not found"
            action={<LinkButton to="/assignees">Back to assignees</LinkButton>}
          >
            They may have been deleted in another tab.
          </EmptyState>
        ) : loadError ? (
          <Alert>
            {loadError instanceof Error ? loadError.message : 'Could not load this person.'}
          </Alert>
        ) : id && !assignee ? (
          <SkeletonRows count={5} rowClassName="h-14" />
        ) : (
          <AssigneeForm
            // Remount once the record arrives, so the form seeds from it rather
            // than from the empty state it mounted with.
            key={assignee?.id ?? 'new'}
            {...(assignee ? { assignee } : {})}
            onSaved={() => navigate('/assignees')}
            onCancel={() => navigate('/assignees')}
            onDeleted={() => navigate('/assignees')}
          />
        )}
      </div>
    </div>
  );
}
